// btw-front/src/components/admin/dashboard/DeletedDealsTab.jsx
// [삭제된 딜] 탭 — 열람 전용(복구 기능 없음)
//
// **심사 반려된 딜도 여기 모인다** — 반려는 소프트 삭제를 동반한다. 그 경우 deletedReason 은
// 경로만 밝히는 고정 문구("심사 반려로 삭제됨")이고 사유 전문은 reviewRejectReason 에 있다.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Modal } from "antd";
import { Badge } from "../../ui/Badge";
import { EmptyState } from "../../ui/EmptyState";
import { Input } from "../../ui/Input";
import * as adminDeals from "../../../api/adminDeals";
import { filterByKeyword } from "../../../lib/keywordFilter";
import { useQueryState } from "../../../lib/useQueryState";

export default function DeletedDealsTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // 키워드(파트너사·고객사·영업대표) — URL 에 보관해 딜 상세에서 돌아와도 유지.
  // 파트너사는 목록 '파트너/등록' 컬럼 표시값(관리자 등록 딜은 등록 관리자 회사명)으로 찾는다
  const [q, setQ] = useQueryState("q");
  const visible = useMemo(
    () => filterByKeyword(items, q, ["displayCompanyName", "customerCompany", "salesRepName"]),
    [items, q],
  );

  useEffect(() => {
    let cancelled = false;
    adminDeals
      .listDeleted()
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 사유 보기 — 목록에서 클릭 시 전문 표시
  function showReason(d) {
    Modal.info({
      title: t("admin.deletedDeals.reasonTitle"),
      content: (
        <div className="text-sm">
          <p className="text-xs text-slate-500">
            {d.projectName} · {t("admin.deletedDeals.deletedAt")}: {d.deletedAt || "-"} ·{" "}
            {t("admin.deletedDeals.deletedBy")}: {d.deletedByName || "-"}
          </p>
          <p className="mt-3 whitespace-pre-wrap text-slate-800">
            {d.deletedReason || t("admin.deletedDeals.noReason")}
          </p>
          {/* 반려 딜은 사유 전문을 따로 들고 있다 — 삭제 사유(고정 문구)만으로는 알 수 없다 */}
          {d.isRejected && (
            <p className="mt-3 whitespace-pre-wrap border-t border-slate-200 pt-3 text-slate-800">
              <span className="text-xs text-slate-500">{t("admin.deletedDeals.rejectReason")}</span>
              <br />
              {d.reviewRejectReason || t("admin.deletedDeals.noReason")}
            </p>
          )}
        </div>
      ),
      okText: t("admin.deletedDeals.reasonClose"),
    });
  }

  return (
    <section className="space-y-4">
      <Input
        name="search"
        placeholder={t("admin.deletedDeals.searchPlaceholder")}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="w-full sm:w-72"
      />
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {loading ? (
          <p className="py-10 text-center text-sm text-slate-400">{t("common.loading")}</p>
        ) : visible.length === 0 ? (
          <EmptyState
            title={
              items.length > 0 ? t("admin.deletedDeals.searchEmpty") : t("admin.deletedDeals.empty")
            }
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium">
                  {t("admin.deletedDeals.deletedAt")}
                </th>
                <th className="px-4 py-2.5 text-left font-medium">
                  {t("admin.deletedDeals.company")}
                </th>
                <th className="px-4 py-2.5 text-left font-medium">
                  {t("admin.deletedDeals.projectName")}
                </th>
                <th className="px-4 py-2.5 text-left font-medium">
                  {t("admin.deletedDeals.customer")}
                </th>
                <th className="px-4 py-2.5 text-left font-medium">
                  {t("admin.deletedDeals.salesRep")}
                </th>
                <th className="px-4 py-2.5 text-left font-medium">
                  {t("admin.deletedDeals.status")}
                </th>
                <th className="px-4 py-2.5 text-left font-medium">
                  {t("admin.deletedDeals.deletedBy")}
                </th>
                <th className="px-4 py-2.5 text-right font-medium">
                  {t("admin.deletedDeals.reason")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map((d) => (
                <tr
                  key={d.id}
                  onClick={() => navigate(`/partners/deals/${d.id}`)}
                  className="cursor-pointer transition hover:bg-slate-50"
                >
                  <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">
                    {d.deletedAt || "-"}
                  </td>
                  <td className="break-words px-4 py-2.5 text-slate-700">
                    {d.displayCompanyName || "-"}
                  </td>
                  <td className="break-words px-4 py-2.5 font-medium text-slate-900">
                    {d.projectName}
                  </td>
                  <td className="break-words px-4 py-2.5 text-slate-600">{d.customerCompany}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">
                    {d.salesRepName || "-"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <Badge tone={d.status}>{t(`partners.deals.status.${d.status}`)}</Badge>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">
                    {d.deletedByName || "-"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        showReason(d);
                      }}
                      className="text-xs text-accent underline-offset-2 hover:underline"
                    >
                      {d.deletedReason
                        ? t("admin.deletedDeals.reasonView")
                        : t("admin.deletedDeals.noReasonShort")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
